import { SurveyUserRoles } from '@constants/user';
import { useRemoveTemplateWorkgroupMutation } from '@api/survey-templates';
import { generateToast, toastFromError } from '@g17eco/molecules/toasts';
import { DelegateTemplateWorkgroupModal } from './DelegateTemplateWorkgroupModal';
import { SurveyWorkgroup } from '@g17eco/types/workgroup';
import { DataScopeAccess } from '@g17eco/types/surveyDataScope';
import { SurveyTemplateMinData } from '../../types';
import { MetricGroup } from '@g17eco/types/metricGroup';
import { WorkgroupsTableBase } from '@features/delegation';

const getMetricCount = (
  { permission, utrvIds: workgroupUtrvIds }: SurveyWorkgroup,
  role: SurveyUserRoles.Stakeholder | SurveyUserRoles.Verifier,
): string | number => {
  if (!permission.roles.includes(role)) {
    return '-';
  }

  if (permission.access === DataScopeAccess.Full) {
    return 'All';
  }

  if (permission.access === DataScopeAccess.None || !permission.scope) {
    return 0;
  }

  return workgroupUtrvIds.length;
};

interface Props {
  template: SurveyTemplateMinData;
  workgroups: SurveyWorkgroup[];
  metricGroups: MetricGroup[];
}

export const TemplateWorkgroupsTable = ({ template, workgroups, metricGroups }: Props) => {
  const [removeWorkgroup] = useRemoveTemplateWorkgroupMutation();

  const handleDelete = (workgroupId: string) => {
    return removeWorkgroup({ templateId: template._id, workgroupId })
      .unwrap()
      .then(() => {
        generateToast({
          title: 'Workgroup removed',
          color: 'success',
          message: 'Workgroup has been removed from the template',
        });
      })
      .catch((e) => {
        toastFromError({ error: e, title: 'Workgroup removal failed' });
      });
  };

  return (
    <WorkgroupsTableBase
      entity={template}
      workgroups={workgroups}
      metricGroups={metricGroups}
      entityLabel='template'
      handlers={{ onDelete: handleDelete }}
      getCountDisplay={(workgroup, role) => getMetricCount(workgroup, role)}
      renderDelegateModal={({ isOpen, toggle, editingWorkgroup }) => (
        <DelegateTemplateWorkgroupModal
          key={`delegate-workgroup-modal-${editingWorkgroup?._id}`}
          isOpen={isOpen}
          toggle={toggle}
          template={template}
          metricGroups={metricGroups}
          delegatedWorkgroups={workgroups}
          editingWorkgroup={editingWorkgroup}
        />
      )}
    />
  );
};
